import React, { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { faQuestionCircle } from '@fortawesome/pro-light-svg-icons/faQuestionCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Button from 'epn-ui/umd/dist/components/Button';
import Checkbox, { CheckboxChangeEvent } from 'epn-ui/umd/dist/components/CheckboxInput';
import Form from 'epn-ui/umd/dist/components/Form';
import InputText from 'epn-ui/umd/dist/components/InputText';
import notification from 'epn-ui/umd/dist/components/Notification';
import Select from 'epn-ui/umd/dist/components/Select';
import Skeleton from 'epn-ui/umd/dist/components/Skeleton';
import TooltipPro from 'epn-ui/umd/dist/components/TooltipPro';

import BaseSemanticLink from '@base/BaseSemanticLink';
import BaseDragAndDrop from '@components/base/BaseDragAndDropAttach';

import api from '@api';

import useI18n from '@i18n';

import { CREATE_CREATIVE_ERRORS, INPUT_NAMES_BY_CREATIVE_ERROR } from '@api/errors';
import { companyName } from '@config/globalDomain';
import routes from '@config/routes.json';
import { subIdRegexp } from '@components/pages/tools/affiliate-links/config';
import { WITHOUT_SPACE_START_END } from '@utils/regex';
import { ORD_FORM_OPTIONS, ORD_FORM_TYPES } from '../config';

// TYPES
import { IFormMarkingProps } from '../interfaces';

const FormMarking: React.FC<IFormMarkingProps> = ({
    form,
    offersIsLoading,
    placementsIsLoading,
    offers,
    onChangeOffer,
    placements,
    reductionList,
    reductionListLoading,
    onChangeReduction,
    reductionType,
    reductionsNeed,
    setReductionsNeed,
    onSubmitForm,
    formError,
    offerId,
    isLoading,
    isAli,
    requiredPlacement,
}) => {
    const [subIdInputs, setSubIdInputs] = useState<string[]>([]);
    const [searchOffer, setSearchOffer] = useState<boolean>(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const [clearFormAfterSubmit, setClearFormAfterSubmit] = useState<boolean>(true);
    const { t } = useI18n('Marking');
    const { t: tCommon } = useI18n('Common');
    const { t: tTools } = useI18n('Tools');
    const { t: tErrors } = useI18n('Errors');
    const ordForm = Form.useWatch('ordForm', form);
    const link = Form.useWatch('link', form);

    // Get Offer by link
    const getOfferIdByLink = useCallback(async (url: string): Promise<string | null> => {
        try {
            const { data } = await api.offer.getOfferByLink({ params: { link: url } });
            // если ссылка принадлежит офферу, то берем id из первого подходящего
            const { id } = data?.[0];
            return id;
        } catch (error) {
            return null;
        }
    }, []);

    // Проверка ссылки на соответствие офферу
    const validateLink = debounce(async (inputLink: string) => {
        try {
            if (new URL(inputLink)) {
                setSearchOffer(true);
                const id = await getOfferIdByLink(inputLink);
                const hasOffer = offers.some(offer => Number(offer.id) === Number(id));
                if (!id || !hasOffer) {
                    form.setFields([
                        {
                            name: 'link',
                            errors: [t('The link does not relate to the offer')],
                        },
                    ]);
                    return;
                }
                form.setFields([{ name: 'link', errors: [] }]);
                form.setFields([{ name: 'offerId', value: id }]);
                onChangeOffer(id);
            }
        } catch (error) {
            form.setFields([
                {
                    name: 'link',
                    errors: [t('Incorrect link for the offer')],
                },
            ]);
        } finally {
            setSearchOffer(false);
        }
    }, 700);

    // Ищу оффер под ссылку
    useEffect(() => {
        if (link && link.length > 0) {
            validateLink(link);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [link]);

    const validateOffer = useCallback(async () => {
        if (!link) return;
        try {
            const id = await getOfferIdByLink(link);
            if (Number(id) !== Number(offerId)) {
                form.setFields([
                    {
                        name: 'link',
                        errors: [t('The link does not relate to the offer')],
                    },
                ]);
                return;
            }
            form.setFields([{ name: 'link', errors: [] }]);
        } catch (error) {
            form.setFields([
                {
                    name: 'link',
                    errors: [t('Incorrect link for the offer')],
                },
            ]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getOfferIdByLink, link, offerId]);

    const handleFileError = useCallback(() => {
        if (Array.isArray(formError)) {
            formError.forEach(item => {
                if (item.error === 'ordRim') {
                    setFileError(tErrors(CREATE_CREATIVE_ERRORS[item.error] || item.description));
                    return;
                }
                // TODO Нужно чтоб бекенд возвращал поле ошибки
                if (INPUT_NAMES_BY_CREATIVE_ERROR[item.error]) {
                    form.setFields([
                        {
                            name: INPUT_NAMES_BY_CREATIVE_ERROR[item.error],
                            errors: [
                                ...form.getFieldError(INPUT_NAMES_BY_CREATIVE_ERROR[item.error]),
                                tErrors(CREATE_CREATIVE_ERRORS[item.error]),
                            ],
                        },
                    ]);
                } else {
                    notification.error({
                        message: `${item.error} ${tErrors(
                            CREATE_CREATIVE_ERRORS[item.error] || item.error_description,
                        )}`,
                    });
                }
            });
        } else {
            form.resetFields();
            setFileError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formError]);

    // Проверяю ссылку под оффер
    useEffect(() => {
        if (offerId) {
            debounce(async () => {
                setSearchOffer(true);
                await validateOffer();
                setSearchOffer(false);
            }, 700)();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offerId]);

    useEffect(() => {
        handleFileError();
    }, [handleFileError]);

    const ordFormOptions = ORD_FORM_OPTIONS(t);

    const preparedOffers = useMemo(
        () =>
            offers.map(item => ({
                label: item.name,
                value: item.id,
            })),
        [offers],
    );

    const preparedPlacements = useMemo(
        () =>
            placements.map(item => ({
                label: item.url,
                value: item.id,
            })),
        [placements],
    );

    // Добавляем инпуты для сабов
    const onAddSubId = index => {
        if (subIdInputs.length >= 4) return;
        setSubIdInputs(prev => [...prev, `subid-${index}`]);
    };

    const onAddFile = useCallback(
        newFiles => {
            if (!newFiles.length) {
                form.setFieldsValue({ ordRim: [] });
                return;
            }
            form.setFieldsValue({ ordRim: newFiles[0].originFileObj });
        },
        [form],
    );

    const onChangeReductionType = (value: string) => {
        onChangeReduction(value);
    };

    const onCheckReductionsType = (e: CheckboxChangeEvent) => {
        setReductionsNeed(e.target.checked);
    };

    const onChangeClearFormAfterSubmit = (e: CheckboxChangeEvent) => {
        setClearFormAfterSubmit(e.target.checked);
    };

    const onSubmit = async formData => {
        // Проставляю дефолтные поля
        const preparedForm = {
            ...formData,
            type: 'deeplink',
            offerId: formData.offerId || preparedOffers[0].value,
            placementId: formData.placementId || preparedPlacements[0]?.value,
            ordForm: formData.ordForm || ordFormOptions[0]?.value,
        };
        // Проверяю добавлены ли файлы
        if (preparedForm.ordForm !== ORD_FORM_TYPES.text) {
            if (!formData.ordRim) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                form.setFields([{ name: 'ordRim', errors: [t('Add an image')] }]);
                return;
            }
        }

        await onSubmitForm(preparedForm);

        if (clearFormAfterSubmit) {
            form.resetFields();
        }
        setSubIdInputs([]);
    };

    // Ошибка, если нет площадок для данного оффера
    const noPlacementsError =
        offerId &&
        !placementsIsLoading &&
        Array.isArray(placements) &&
        !placements.length &&
        requiredPlacement;

    // Обязательное поле для этих типов
    const ordTextIsRequires =
        ordForm === ORD_FORM_TYPES.text || ordForm === ORD_FORM_TYPES.textGraphic;

    const ordRimIsRequired =
        ordForm === ORD_FORM_TYPES.banner || ordForm === ORD_FORM_TYPES.textGraphic;

    return (
        <Form
            name="form_marking"
            onFinish={onSubmit}
            form={form}
            initialValues={{ ordForm: ordFormOptions[0]?.value }}
        >
            <Form.Item
                label={<span className="text-blueGray-500">{tTools('Insert your link here')}</span>}
                name="link"
                required
                rules={[
                    {
                        required: ordTextIsRequires,
                        whitespace: true,
                        message: tCommon('Required field'),
                    },
                ]}
            >
                <InputText placeholder={tTools('Example: https://aliexpress.ru')} />
            </Form.Item>
            <Form.Item
                label={
                    <>
                        <span className="text-blueGray-500">{t('Name of creative')}&nbsp;</span>
                        <TooltipPro
                            title={t(
                                'Add the name of your creative for convenience. It is used only in ePN',
                                { companyName },
                            )}
                            //  @ts-expect-error TODO Нужно поправить пропсы компонента
                            overlayInnerStyle={{ minWidth: '305px' }}
                        >
                            <FontAwesomeIcon
                                className="cursor-pointer hover:text-blueGray-800"
                                icon={faQuestionCircle}
                            />
                        </TooltipPro>
                    </>
                }
                name="description"
                rules={[
                    {
                        max: 100,
                        message: t('Maximum length of characters', { max: 100 }),
                    },
                ]}
            >
                <InputText placeholder={tTools('for example: jewelry in footer')} />
            </Form.Item>
            <Form.Item
                label={<span className="text-blueGray-500">{tCommon('Choose offer')}</span>}
                name="offerId"
                required
            >
                {offersIsLoading ? (
                    <Skeleton.Input active className="!w-full" />
                ) : (
                    <Select
                        options={preparedOffers}
                        defaultValue={preparedOffers[0]?.value}
                        onChange={onChangeOffer}
                        loading={searchOffer}
                        filterOption={(input, option) =>
                            (option?.label.toString().toLowerCase() ?? '').includes(
                                input.toLowerCase(),
                            )
                        }
                        notFoundContent={tCommon('Nothing found')}
                    />
                )}
            </Form.Item>
            <Form.Item
                label={<span className="text-blueGray-500">{t('Choose a traffic source')}</span>}
                name="placementId"
                help={
                    <span className="text-p3 text-blueGray-500">
                        {t('Creative can only be used for one offer and placement')}
                    </span>
                }
                rules={[{ required: requiredPlacement }]}
            >
                {placementsIsLoading ? (
                    <Skeleton.Input active className="!w-full" />
                ) : (
                    <Select
                        options={preparedPlacements}
                        notFoundContent={tCommon('Nothing found')}
                    />
                )}
            </Form.Item>
            {noPlacementsError && (
                <div className="mt-2 mb-6 text-p2">
                    <span className="text-red-400">
                        {t('Please add your traffic source for this offer')}.
                    </span>{' '}
                    <BaseSemanticLink
                        className="underline text-blueGray-800"
                        to={routes.placements.add}
                    >
                        {tTools('Add traffic source')}
                    </BaseSemanticLink>
                </div>
            )}

            <div className="relative">
                <Form.Item
                    label={
                        <span className="text-blueGray-500">
                            {tTools('Insert subid if needed')}
                        </span>
                    }
                    name="sub"
                    rules={[
                        {
                            pattern: subIdRegexp,
                            message: tTools('Sub may only contain the following symbols'),
                        },
                    ]}
                >
                    <InputText placeholder="sub" />
                </Form.Item>
                <button
                    className="absolute right-0 flex items-center justify-center w-10 h-10 top-5"
                    id="AffiliateInputsAddSubIdButton"
                    type="button"
                    onClick={onAddSubId}
                >
                    <FontAwesomeIcon className="block text-blueGray-800 text-sub" icon={faPlus} />
                </button>
            </div>
            {Object.keys(subIdInputs).map((_item, index) => (
                <Form.Item
                    name={`sub${index + 2}`}
                    key={`sub${index + 2}`}
                    rules={[
                        {
                            pattern: subIdRegexp,
                            message: tTools('Sub may only contain the following symbols'),
                        },
                    ]}
                >
                    <InputText placeholder={`sub${index + 2}`} />
                </Form.Item>
            ))}
            <Form.Item
                label={<span className="text-blueGray-500">{t('Select creative type')}</span>}
                name="ordForm"
                required
                help={
                    <span className="text-p3 text-blueGray-500">
                        {t(
                            'Example: for a post with a text and an image, select Text and Graphics',
                        )}
                    </span>
                }
                className="mb-6"
            >
                <Select options={ordFormOptions} notFoundContent={tCommon('Nothing found')} />
            </Form.Item>
            <div className="mb-6">
                <Form.Item
                    label={<span className="text-blueGray-500">{t('Enter creative text')}</span>}
                    name="ordText"
                    required={ordTextIsRequires}
                    className="mb-0"
                    rules={[
                        {
                            required: ordTextIsRequires,
                            whitespace: true,
                            message: tCommon('Required field'),
                        },
                        {
                            max: 40000,
                            message: t('Maximum length of characters', { max: 40000 }),
                        },
                    ]}
                >
                    <InputText.TextArea placeholder={t('Example, my creative')} />
                </Form.Item>
                <span className="text-p3 text-blueGray-500">
                    {t(
                        'The field is required for creative types: text block and text-graphic block. Up to 40,000 characters available',
                    )}
                </span>
            </div>
            {ordRimIsRequired && (
                <>
                    <Form.Item
                        name="ordRim"
                        label={<span className="text-blueGray-500">{t('Choose file')}</span>}
                        required={ordRimIsRequired}
                    >
                        <BaseDragAndDrop
                            allowedTypes="image/jpg, image/jpeg, image/png, image/heic, image/heif"
                            maxFilesSizeMb={10}
                            error={fileError}
                            onChange={onAddFile}
                            isLoading={isLoading && clearFormAfterSubmit}
                            multiple={false}
                        />
                    </Form.Item>
                    <Form.Item
                        label={
                            <span className="text-blueGray-500">
                                {t('Creative image description')}
                            </span>
                        }
                        name="ordRimDescription"
                        required={ordRimIsRequired}
                        rules={[
                            {
                                required: true,
                                whitespace: true,
                                message: tCommon('Required field'),
                            },
                            {
                                max: 1000,
                                message: t('Maximum length of characters', { max: 1000 }),
                            },
                            {
                                pattern: WITHOUT_SPACE_START_END,
                                message: t(
                                    'There should be no space or line breaks at the beginning or end',
                                ),
                            },
                        ]}
                    >
                        <InputText.TextArea placeholder={t('For example: Photo of a cat')} />
                    </Form.Item>
                </>
            )}
            <Form.Item
                label={
                    <span className="text-blueGray-500">
                        {t(
                            'Add a description in the format Brand name. Type of product or service',
                        )}
                    </span>
                }
                name="ordDescription"
                required
                rules={[
                    {
                        required: true,
                        whitespace: true,
                        message: tCommon('Required field'),
                    },
                    {
                        max: 1000,
                        message: t('Maximum length of characters', { max: 1000 }),
                    },
                ]}
            >
                <InputText placeholder={t('Example: Brand Puma. Running shoes')} />
            </Form.Item>
            <div className="flex flex-col pb-4 xs:items-center xs:flex-row xs:justify-between">
                {reductionListLoading ? (
                    <Skeleton.Input active className="!w-full" />
                ) : (
                    <div>
                        <Checkbox
                            checked={clearFormAfterSubmit}
                            onChange={onChangeClearFormAfterSubmit}
                        >
                            {t('Clear form after submit')}
                        </Checkbox>
                    </div>
                )}
            </div>
            <div className="flex flex-col xs:items-center xs:flex-row xs:justify-between">
                {!isAli ? (
                    <>
                        {reductionListLoading ? (
                            <Skeleton.Input active className="!w-full" />
                        ) : (
                            <div>
                                <Checkbox onChange={onCheckReductionsType}>
                                    {tTools('Shorten link via')}:
                                </Checkbox>
                                <Select
                                    id="reducers-list"
                                    options={reductionList}
                                    value={reductionType}
                                    disabled={!reductionsNeed}
                                    onChange={onChangeReductionType}
                                    notFoundContent={tCommon('Nothing found')}
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <div />
                )}
                <Button
                    type="primary"
                    htmlType="submit"
                    className="mt-4"
                    disabled={!form.isFieldsTouched()}
                >
                    {tCommon('Send')}
                </Button>
            </div>
        </Form>
    );
};

export default FormMarking;
